// frontend/pages/OnusRegistradas.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./OnusRegistradas.css";
import WanModal from "../onus_components/WanModal";
import Toast from "../components/Toast";
import Tr069Modal from "../onus_components/Tr069Modal";
import AccionOntModal from "../onus_components/AccionOntModal";
import WanAccessModal from "../onus_components/WanAccessModal";
import DeleteOnuModal from "../onus_components/DeleteOnuModal";
import OpticalPowerModal from "../onus_components/OpticalPowerModal";
import OnlineStatusModal from "../onus_components/OnlineStatusModal";
import { authFetch } from "../utils/authFetch";
import {
  BarraFiltros, EncabezadoPagina, Iconos, Insignia,
} from "../componentes/base";


export default function ONUsAutorizadas() {
  const [onus, setOnus] = useState([]);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null); // { tipo, mensaje }

  // paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // fila seleccionada
  const [selectedId, setSelectedId] = useState(null);

  // toolbar iOS
  const [actionBar, setActionBar] = useState(null); // { onu, rect }
  const closeActionBar = () => setActionBar(null);

  // modal WAN
  const [wanModal, setWanModal] = useState({ open: false, onu: null });
  const closeWanModal = () => setWanModal({ open: false, onu: null });

  //modal Tr069
  const [tr069Modal, setTr069Modal] = useState({ open: false, onu: null });
  const closeTr069Modal = () => setTr069Modal({ open: false, onu: null });

  //modal RESET
  // `accion` decide qué se manda: "reiniciar" es `ont reset` y "fabrica"
  // es `ont factory-setting-restore`. Antes los dos botones abrían el
  // mismo modal y los dos hacían lo segundo, así que «Reiniciar» le
  // borraba al cliente el PPPoE, el WiFi y el TR069.
  const [factoryModal, setFactoryModal] = useState(
    { open: false, onu: null, accion: "reiniciar" });
  const closeFactoryModal = () => setFactoryModal({ open: false, onu: null });

  //modal WAN ACCESS
  const [wanAccessModal, setWanAccessModal] = useState({ open: false, onu: null });
  const closeWanAccessModal = () => setWanAccessModal({ open: false, onu: null });

  //modal DELETE
  const [deleteModal, setDeleteModal] = useState({ open: false, onu: null });
  const closeDeleteModal = () => setDeleteModal({ open: false, onu: null });

  //modal POWER
  const [powerModal, setPowerModal] = useState({ open: false, onu: null });
  const closePowerModal = () => setPowerModal({ open: false, onu: null });

  //modal ONLINE
  const [onlineModal, setOnlineModal] = useState({ open: false, onu: null });
  const closeOnlineModal = () => setOnlineModal({ open: false, onu: null });

  const anyModalOpen =
    wanModal.open || tr069Modal.open || factoryModal.open || wanAccessModal.open || deleteModal.open || powerModal.open || onlineModal.open;

  useEffect(() => {
    authFetch("/api/onus/registradas")
      .then((res) => res.json())
      .then((data) => {
        if (data.ok) setOnus(data.onus || []);
      })
      .catch((err) => console.error(err));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return onus;
    return onus.filter((o) =>
      Object.values(o).join(" ").toLowerCase().includes(q)
    );
  }, [onus, search]);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const start = (pageSafe - 1) * pageSize;
  const end = Math.min(start + pageSize, total);
  const pageRows = filtered.slice(start, end);

  function goTo(p) {
    setPage(Math.min(Math.max(1, p), totalPages));
  }

  const pageButtons = useMemo(() => {
    const btns = [];
    const maxShown = 7;

    if (totalPages <= maxShown) {
      for (let i = 1; i <= totalPages; i++) btns.push(i);
      return btns;
    }

    const left = Math.max(1, pageSafe - 1);
    const right = Math.min(totalPages, pageSafe + 1);

    btns.push(1);
    if (left > 2) btns.push("...");

    for (let i = left; i <= right; i++) {
      if (i !== 1 && i !== totalPages) btns.push(i);
    }

    if (right < totalPages - 1) btns.push("...");
    btns.push(totalPages);

    return btns.filter((v, idx, arr) => idx === 0 || v !== arr[idx - 1]);
  }, [pageSafe, totalPages]);

  function openToolbarAnchoredToIdCell(onu, e) {
    const row = e.currentTarget.closest("tr");
    if (!row) return;
    const idCell = row.querySelector("td:nth-child(2)");
    const rect = (idCell || row).getBoundingClientRect();
    setActionBar({ onu, rect });
  }

  function selectRow(onu, e) {
    if (anyModalOpen) return;
    setSelectedId(onu.id);
    openToolbarAnchoredToIdCell(onu, e);
  }

  function toggleCheckbox(onu, e) {
    e.stopPropagation();
    if (anyModalOpen) return;

    const isSame = selectedId === onu.id;
    const next = isSame ? null : onu.id;
    setSelectedId(next);

    if (!isSame) {
      const row = e.currentTarget.closest("tr");
      const idCell = row?.querySelector("td:nth-child(2)");
      const rect = (idCell || row).getBoundingClientRect();
      setActionBar({ onu, rect });
    } else {
      closeActionBar();
    }
  }

  function handleAccion(tipo, onu) {
    // Normalizamos acciones para evitar errores por strings
    const t = String(tipo || "").trim().toLowerCase();

    if (t === "wans") {
      closeActionBar();
      setSelectedId(null);
      setWanModal({ open: true, onu });
      return;
    }

    if (t === "tr069") {
      closeActionBar();
      setSelectedId(null);
      setTr069Modal({ open: true, onu });
      return;
    }

    if (t === "reiniciar" || t === "reset") {
      closeActionBar();
      setSelectedId(null);
      setFactoryModal({ open: true, onu, accion: t === "reiniciar" ? "reiniciar" : "fabrica" });
      return;
    }

    if (t === "wan-access") {
      closeActionBar();
      setSelectedId(null);
      setWanAccessModal({ open: true, onu });
      return;
    }

    if (t === "eliminar-onu") {
      closeActionBar();
      setSelectedId(null);
      setDeleteModal({ open: true, onu });
      return;
    }

    if (t === "potencia") {
      closeActionBar();
      setSelectedId(null);
      setPowerModal({ open: true, onu });
      return;
    }

    if (t === "en-linea") {
      closeActionBar();
      setSelectedId(null);
      setOnlineModal({ open: true, onu });
      return;
    }

    console.log("Acción:", tipo, "sobre ONU:", onu);
    closeActionBar();
  }

  useEffect(() => {
    if (!actionBar || anyModalOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") closeActionBar();
    };

    const onPointerDown = (e) => {
      const bar = document.querySelector(".ios-actionbar");
      if (bar && bar.contains(e.target)) return;
      closeActionBar();
    };

    const onScroll = () => closeActionBar();
    const onResize = () => closeActionBar();

    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [actionBar, anyModalOpen]);

  // ✅ Cerrar actionbar si se abre cualquier modal
  useEffect(() => {
    if (anyModalOpen) closeActionBar();
  }, [anyModalOpen]);

  const actionBarPortal = actionBar
    ? createPortal(
        <IosActionBar
          rect={actionBar.rect}
          onu={actionBar.onu}
          onClose={closeActionBar}
          onAction={handleAccion}
        />,
        document.body
      )
    : null;

  return (
    <div className="onus-wrap">
      <div className="onus-page">
        {/* HEADER */}
        <EncabezadoPagina
          icono={Iconos.onus}
          titulo="ONUs Autorizadas"
          resumen={`${total} registradas · toca una fila para sus acciones`}
        />

        <BarraFiltros
          valor={search}
          onBuscar={setSearch}
          placeholder="Buscar por cliente, serie, modelo…"
          extra={
            <div className="hc-right">
              <label className="page-size">
                <span>Filas:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}/pág
                    </option>
                  ))}
                </select>
              </label>

              <div className="range">
                {total === 0 ? "0" : `${start + 1}-${end}`} de {total}
              </div>
            </div>
          }
        />

        {/* TABLA */}
        <div className="tabla-card">
          <div className="tabla-container">
            <table className="tabla-onus">
              <thead>
                <tr>
                  <th style={{ width: 52 }}></th>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>F/S/P</th>
                  <th>ONT ID</th>
                  <th>Service Port</th>
                  <th>No. Serie</th>
                  <th>Modelo</th>
                  <th>Estado</th>
                  <th>Creado</th>
                </tr>
              </thead>

              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center", padding: 16 }}>
                      Sin resultados
                    </td>
                  </tr>
                ) : (
                  pageRows.map((onu) => {
                    const selected = selectedId === onu.id;
                    const estadoNorm = String(onu.estado || "").trim().toLowerCase();

                    return (
                      <tr
                        key={onu.id}
                        className={selected ? "row-selected" : ""}
                        onClick={(e) => selectRow(onu, e)}
                        title="Click para acciones"
                      >
                        <td className="check-cell" onClick={(e) => e.stopPropagation()}>
                          <label className="ios-check">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => toggleCheckbox(onu, e)}
                            />
                            <span className="ios-check-ui" aria-hidden="true" />
                          </label>
                        </td>

                        <td>{onu.id}</td>
                        <td>{onu.cliente_id}</td>
                        <td>{onu.fsp}</td>
                        <td>{onu.ont_id}</td>
                        <td>{onu.service_port}</td>
                        <td>{onu.serial}</td>
                        <td>{onu.modelo}</td>

                        <td>
                          {(() => {
                            const enLinea = ["online", "activo"].includes(estadoNorm);
                            return (
                              <Insignia
                                variante={enLinea ? "activo" : "cortado"}
                                icono={enLinea ? Iconos.enLinea : Iconos.fueraDeLinea}
                                texto={onu.estado}
                              />
                            );
                          })()}
                        </td>

                        <td>{onu.creado ? new Date(onu.creado).toLocaleString() : "-"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="pager pager-bottom">
            <button className="pager-btn" onClick={() => goTo(1)} disabled={pageSafe === 1} title="Primera" aria-label="Primera página">
              <Iconos.primera size={15} strokeWidth={2} />
            </button>
            <button className="pager-btn" onClick={() => goTo(pageSafe - 1)} disabled={pageSafe === 1} title="Anterior" aria-label="Página anterior">
              <Iconos.atras size={15} strokeWidth={2} />
            </button>

            <div className="pager-pages">
              {pageButtons.map((p, idx) =>
                p === "..." ? (
                  <span className="pager-ellipsis" key={`e-${idx}`}>…</span>
                ) : (
                  <button
                    key={p}
                    className={`pager-page ${p === pageSafe ? "active" : ""}`}
                    onClick={() => goTo(p)}
                  >
                    {p}
                  </button>
                )
              )}
            </div>

            <button className="pager-btn" onClick={() => goTo(pageSafe + 1)} disabled={pageSafe === totalPages} title="Siguiente" aria-label="Página siguiente">
              <Iconos.adelante size={15} strokeWidth={2} />
            </button>
            <button className="pager-btn" onClick={() => goTo(totalPages)} disabled={pageSafe === totalPages} title="Última" aria-label="Última página">
              <Iconos.ultima size={15} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

      {actionBarPortal}

      <WanModal
        open={wanModal.open}
        onu={wanModal.onu}
        onClose={closeWanModal}
        onSuccess={(msg) => setToast({ tipo: "success", mensaje: msg || "WAN aplicada con éxito ✔" })}
      />

      <Tr069Modal
        open={tr069Modal.open}
        onu={tr069Modal.onu}
        onClose={closeTr069Modal}
        onSuccess={(msg) => setToast({ tipo: "success", mensaje: msg || "TR069 activado ✔" })}
      />

      <AccionOntModal
        open={factoryModal.open}
        onu={factoryModal.onu}
        accion={factoryModal.accion}
        onClose={closeFactoryModal}
        onSuccess={(msg) => setToast({ tipo: "success", mensaje: msg || "Hecho ✔" })}
      />

      <WanAccessModal
        open={wanAccessModal.open}
        onu={wanAccessModal.onu}
        onClose={closeWanAccessModal}
        onSuccess={(msg) => setToast({ tipo: "success", mensaje: msg || "WAN access aplicado ✔" })}
      />

      <OpticalPowerModal
        open={powerModal.open}
        onu={powerModal.onu}
        onClose={closePowerModal}
      />

      <OnlineStatusModal
        open={onlineModal.open}
        onu={onlineModal.onu}
        onClose={closeOnlineModal}
      />

      <DeleteOnuModal
        open={deleteModal.open}
        onu={deleteModal.onu}
        onClose={closeDeleteModal}
        onSuccess={async (msg) => {
          setToast({ tipo: "success", mensaje: msg || "ONU eliminada ✔" });

          // recargar tabla
          try {
            const res = await authFetch("/api/onus/registradas");
            const data = await res.json();
            if (data.ok) setOnus(data.onus || []);
          } catch {}
        }}
      />

      {toast && <Toast mensaje={toast.mensaje} tipo={toast.tipo} onClose={() => setToast(null)} />}
    </div>
  );
}

// ======= Toolbar iOS =======
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function IosActionBar({ rect, onu, onClose, onAction }) {
  const isMobile = window.matchMedia?.("(max-width: 760px)")?.matches;

  let style = {};
  if (isMobile) {
    style = { left: "50%", bottom: "18px", transform: "translateX(-50%)" };
  } else {
    const width = 640;
    const top = rect.top + window.scrollY - 70;
    const leftIdeal = rect.left + window.scrollX;
    const left = clamp(leftIdeal, 14 + window.scrollX, window.innerWidth + window.scrollX - width - 14);
    style = { top: Math.max(14 + window.scrollY, top), left };
  }

  return (
    <div className="ios-actionbar" style={style} role="menu" aria-label="Acciones ONU">
      <div className="ios-ab-meta">
        <span className="ios-ab-title">Cliente</span>
        <span className="ios-ab-sub"> {onu?.cliente_id}</span>
      </div>

      <div className="ios-ab-actions">
        <button className="ios-ab-btn b-blue" type="button" onClick={() => onAction("tr069", onu)}>
          <Iconos.ajustes size={15} strokeWidth={1.9} />TR069
        </button>
        <button className="ios-ab-btn b-indigo" type="button" onClick={() => onAction("reiniciar", onu)}>
          <Iconos.reiniciar size={15} strokeWidth={1.9} />Reiniciar
        </button>
        <button className="ios-ab-btn b-teal" type="button" onClick={() => onAction("wans", onu)}>
          <Iconos.olt size={15} strokeWidth={1.9} />WANs
        </button>
        <button className="ios-ab-btn b-cyan" type="button" onClick={() => onAction("reset", onu)}>
          <Iconos.restablecer size={15} strokeWidth={1.9} />De fábrica
        </button>
        <button className="ios-ab-btn b-red" type="button" onClick={() => onAction("wan-access", onu)}>
          <Iconos.equipos size={15} strokeWidth={1.9} />Wan access
        </button>
        <button className="ios-ab-btn b-red-danger" type="button" onClick={() => onAction("eliminar-onu", onu)}>
          <Iconos.eliminar size={15} strokeWidth={1.9} />Eliminar ONU
        </button>
        <button className="ios-ab-btn b-orange" type="button" onClick={() => onAction("potencia", onu)}>
          <Iconos.potencia size={15} strokeWidth={1.9} />Potencia
        </button>
        <button className="ios-ab-btn b-emerald" type="button" onClick={() => onAction("en-linea", onu)}>
          <Iconos.enLinea size={15} strokeWidth={1.9} />En línea
        </button>
      </div>

      <button className="ios-ab-close" type="button" onClick={onClose} aria-label="Cerrar">
        <Iconos.cerrar size={17} strokeWidth={2} />
      </button>
    </div>
  );
}

